import React, { useEffect, useRef, useState } from "react";

/**
 * Q/A dropdown row.
 *
 * Interaction contract:
 *   - Click / Tap / Enter / Space → toggles open (works on all devices).
 *   - Hover (desktop pointer only) → previews open on mouseenter, closes
 *     on mouseleave, unless the item was already click-locked open.
 *   - `prefers-reduced-motion` → transitions removed by the global CSS
 *     kill-switch in index.css, expand is instant.
 *
 * ARIA: the trigger is a real <button aria-expanded aria-controls>, and
 * the body has role=region. Native keyboard focus + tab order come free.
 */
export default function FaqItem({ id, question, children }) {
  const [locked, setLocked] = useState(false);   // click-latched open
  const [hovered, setHovered] = useState(false); // pointer-preview open
  const finePointer = useRef(false);

  useEffect(() => {
    finePointer.current =
      typeof window !== "undefined" &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  }, []);

  const open = locked || (hovered && finePointer.current);
  const bodyId = `faq-a-${id}`;

  const toggle = () => setLocked((v) => !v);
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <div
      className={`faq-item${open ? " is-open" : ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid={`faq-${id}`}
    >
      <button
        type="button"
        className="faq-q"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={toggle}
        onKeyDown={onKey}
      >
        <span className="faq-q-text">{question}</span>
        <svg
          className="faq-chevron"
          viewBox="0 0 20 20"
          width="16"
          height="16"
          aria-hidden="true"
        >
          <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <div id={bodyId} className="faq-a" role="region">
        <div className="faq-a-inner">{children}</div>
      </div>
    </div>
  );
}
